/**
 * Call Attempt Action Functions - Fetch operations for Call Attempts
 *
 * These functions handle all call attempt fetch operations.
 * Uses validated fetch with Zod schemas for runtime type safety.
 */

import { CallAttemptSchema, type CallAttempt } from "@/isomorphic/schemas";
import { getAtomStore } from "@lib/atoms";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import { fetchValidated, fetchValidatedArray } from "@/frontend/lib/validation";
import {
  _apiSessionCallAttemptsAtom,
  sessionCallAttemptsStatusAtom,
  sessionCallAttemptsErrorAtom,
  currentCallAttemptAtom,
  currentCallAttemptStatusAtom,
} from "../core/call-attempts";

// ============================================================================
// FETCH FUNCTIONS
// ============================================================================

/**
 * Fetch all call attempts for a session
 * Endpoint: /api/call-attempts/bySession/${sessionId}
 */
export const fetchSessionCallAttempts = async (jwt: string, sessionId: string): Promise<void> => {
  const store = getAtomStore();

  if (!jwt || !sessionId) {
    return;
  }

  store.set(sessionCallAttemptsStatusAtom, "loading");
  store.set(sessionCallAttemptsErrorAtom, null);

  const result = await fetchValidatedArray(CallAttemptSchema, API_ROUTES.callAttempts.getBySession(sessionId), {
    jwt,
    context: `Session ${sessionId} Call Attempts`,
  });

  if (result.success) {
    store.set(_apiSessionCallAttemptsAtom, result.data);
    store.set(sessionCallAttemptsStatusAtom, "success");
  } else {
    console.error("[CallAttemptActions] Error fetching session call attempts:", result.error);
    store.set(sessionCallAttemptsStatusAtom, "error");
    store.set(sessionCallAttemptsErrorAtom, result.error);
  }
};

/**
 * Fetch a single call attempt by ID
 * Endpoint: /api/call-attempts/[id]/get
 */
export const fetchCallAttempt = async (jwt: string, attemptId: string): Promise<CallAttempt | null> => {
  const store = getAtomStore();

  if (!jwt || !attemptId) {
    return null;
  }

  store.set(currentCallAttemptStatusAtom, "loading");

  const result = await fetchValidated(CallAttemptSchema, API_ROUTES.callAttempts.getById(attemptId), {
    jwt,
    context: `Call Attempt ${attemptId}`,
  });

  if (result.success) {
    store.set(currentCallAttemptAtom, result.data);
    store.set(currentCallAttemptStatusAtom, "success");
    return result.data;
  } else {
    console.error("[CallAttemptActions] Error fetching call attempt:", result.error);
    store.set(currentCallAttemptStatusAtom, "error");
    return null;
  }
};
